/**
 * What currency the client cabinet shows amounts in.
 *
 * A project can be sold in two currencies at once, so a service legitimately
 * reads «207 400 UAH + 4 000 EUR» — correct, and useless to a client asking
 * "so what do I owe". Managers have had a currency switcher in their sidebar
 * all along; this gives the cabinet the same thing. backlog ef0c7e7f / b3f87131.
 *
 * "contract" keeps the per-currency breakdown (the default, and the only form
 * that is exact). Picking a currency converts at today's rate and the amount is
 * prefixed with ≈ — a converted number must never pretend to be the invoice.
 */

export const CABINET_CURRENCIES = [
  { code: "contract", label: "As in contract", short: "±" },
  { code: "UAH", label: "₴ UAH", short: "₴" },
  { code: "USD", label: "$ USD", short: "$" },
  { code: "EUR", label: "€ EUR", short: "€" },
];

// Module-level, same reason as tr(): the money formatters are plain functions
// outside the React tree, called from render.
let CURRENT = "contract";
let RATES = null; // { usd_to_uah, eur_to_uah }

export const setCabinetMoney = (currency, rates) => {
  CURRENT = CABINET_CURRENCIES.some((c) => c.code === currency) ? currency : "contract";
  if (rates) RATES = rates;
};

export const cabinetCurrency = () => CURRENT;

/** Can we actually convert right now? Without rates we must not guess. */
export const canConvert = () => CURRENT !== "contract" && !!(RATES && RATES.usd_to_uah);

const toUah = (amount, from) => {
  const ccy = (from || "USD").toUpperCase();
  if (ccy === "UAH") return amount;
  if (ccy === "USD") return amount * (RATES.usd_to_uah || 0);
  if (ccy === "EUR") return amount * (RATES.eur_to_uah || RATES.usd_to_uah || 0);
  return null; // unknown currency — refuse rather than invent a rate
};

/** Convert into the chosen display currency; null when that can't be done. */
export const convertAmount = (amount, from) => {
  if (!canConvert()) return null;
  const uah = toUah(Number(amount) || 0, from);
  if (uah == null) return null;
  if (CURRENT === "UAH") return uah;
  if (CURRENT === "USD") return RATES.usd_to_uah ? uah / RATES.usd_to_uah : null;
  if (CURRENT === "EUR") {
    const r = RATES.eur_to_uah || RATES.usd_to_uah;
    return r ? uah / r : null;
  }
  return null;
};

/**
 * [["UAH", 207400], ["EUR", 4000]] → one converted total, or null to let the
 * caller fall back to the exact per-currency list.
 */
export const convertEntries = (entries) => {
  if (!canConvert() || !entries?.length) return null;
  let total = 0;
  for (const [ccy, value] of entries) {
    const v = convertAmount(value, ccy);
    if (v == null) return null; // one unknown currency → show the exact list
    total += v;
  }
  return { amount: total, currency: CURRENT, approximate: entries.length > 1 || entries[0][0] !== CURRENT };
};
